import { AppApi, el, panel, button, jellyAmount, fmt } from '../ui/app-api'

export interface RunSummary {
  score: number
  level: number
  jelly: number
  hiScore: number
  newBest: boolean
}

/** Pause overlay over the play screen. */
export class PauseOverlay {
  readonly el: HTMLElement
  constructor(api: AppApi) {
    this.el = el('div', { class: 'overlay is-hidden' })
    const p = panel('Paused')
    p.append(
      button('RESUME', { onClick: () => api.resumeGame() }),
      button('RESTART', { variant: 'secondary', onClick: () => api.restart() }),
      button('QUIT TO MENU', { variant: 'danger', onClick: () => api.quitToMenu() }),
    )
    this.el.append(p)
  }
  show(): void {
    this.el.classList.remove('is-hidden')
  }
  hide(): void {
    this.el.classList.add('is-hidden')
  }
}

/** Game-over overlay with run summary + nickname submission. */
export class GameOverOverlay {
  readonly el: HTMLElement
  private scoreV = el('span', { class: 'v', text: '0' })
  private levelV = el('span', { class: 'v', text: '1' })
  private jellyV = el('div', {})
  private bestV = el('span', { class: 'v', text: '0' })
  private nick: HTMLInputElement
  private submitBtn: HTMLButtonElement
  private newBestTag = el('p', { class: 'panel-title', style: { color: '#ffd93d' }, text: '' })

  constructor(private api: AppApi) {
    this.el = el('div', { class: 'overlay is-hidden' })
    const p = panel('Game Over')

    const grid = el(
      'div',
      { class: 'summary-grid' },
      el('span', { class: 'k', text: 'Score' }),
      this.scoreV,
      el('span', { class: 'k', text: 'Level reached' }),
      this.levelV,
      el('span', { class: 'k', text: 'Jelly earned' }),
      this.jellyV,
      el('span', { class: 'k', text: 'Best' }),
      this.bestV,
    )

    this.nick = el('input', {
      attrs: { type: 'text', maxlength: '20', placeholder: 'Your name for the board' },
    }) as HTMLInputElement
    const nickField = el('div', { class: 'field' }, el('label', { text: 'Nickname' }), this.nick)

    this.submitBtn = button('SUBMIT SCORE', { variant: 'secondary', onClick: () => this.submit() })

    const actions = el(
      'div',
      { class: 'row-actions' },
      button('PLAY AGAIN', { onClick: () => this.playAgain() }),
      button('MENU', { variant: 'ghost', onClick: () => this.toMenu() }),
    )

    p.append(this.newBestTag, grid, nickField, this.submitBtn, actions)
    this.el.append(p)
  }

  private async submit(): Promise<void> {
    this.submitBtn.classList.add('is-disabled')
    this.submitBtn.textContent = 'Saving…'
    await this.api.submitRun(this.nick.value)
    if (this.api.economy.state.online) {
      this.submitBtn.textContent = 'Saved ✓'
      this.api.toast('Score submitted!')
    } else {
      // submitRun already toasted "saved locally" — don't claim a false success.
      this.submitBtn.textContent = 'Saved offline'
    }
  }

  private async playAgain(): Promise<void> {
    await this.api.submitRun(this.nick.value)
    this.api.restart()
  }

  private async toMenu(): Promise<void> {
    await this.api.submitRun(this.nick.value)
    this.api.quitToMenu()
  }

  show(s: RunSummary): void {
    this.scoreV.textContent = fmt(s.score)
    this.levelV.textContent = String(s.level)
    this.jellyV.replaceChildren(jellyAmount(s.jelly))
    this.bestV.textContent = fmt(s.hiScore)
    this.newBestTag.textContent = s.newBest ? '★ New Best Dive! ★' : ''
    this.nick.value = this.api.economy.state.nickname
    this.submitBtn.classList.remove('is-disabled')
    this.submitBtn.textContent = 'SUBMIT SCORE'
    this.el.classList.remove('is-hidden')
  }
  hide(): void {
    this.el.classList.add('is-hidden')
  }
}

/** Big centered transient banner (READY / GO / LEVEL CLEAR). */
export class Banner {
  readonly el: HTMLElement
  private timer: number | null = null
  constructor() {
    this.el = el('div', { class: 'banner is-hidden' })
  }
  show(text: string, durationMs = 0): void {
    this.el.textContent = text
    this.el.classList.remove('is-hidden')
    // restart pop animation
    this.el.style.animation = 'none'
    void this.el.offsetWidth
    this.el.style.animation = ''
    if (this.timer != null) clearTimeout(this.timer)
    if (durationMs > 0) this.timer = window.setTimeout(() => this.hide(), durationMs)
  }
  hide(): void {
    this.el.classList.add('is-hidden')
  }
}
